<template>
    <page-container maxSize="2xl">
        <page-header>Franchise Hub</page-header>
        <div class="flex flex-col gap-6 xl:gap-12 xl:flex-row">
            <div class="shrink-0 xl:w-64">
                <analytics-sidebar
                    v-model="fetchParams"
                    hideCategoryFilter
                    hideResourceFilter
                    hideLockedSnippetsFilter
                    @reset="resetFilters()"
                    :minAllowedDate="minAllowedDate"
                ></analytics-sidebar>
            </div>

            <div class="w-full relative">
                <transition
                    enterFromClass="opacity-0 -translate-y-4"
                    enterToClass="opacity-100 translate-y-0"
                    leaveFromClass="opacity-100 translate-y-0"
                    leaveToClass="opacity-0 -translate-y-4"
                >
                    <div v-if="loading" class="absolute w-full transition pt-20">
                        <loading-bars></loading-bars>
                    </div>
                </transition>

                <div
                    v-if="debouncedFetchParams"
                    class="flex flex-col gap-6"
                    :class="{
                        'opacity-0 pointer-events-none': loading,
                        'opacity-100 delay-200 transition duration-150': !loading,
                    }"
                >
                    <div class="grid gap-6 grid-cols-1 sm:grid-cols-12">
                        <analytics-marketing-subscriptions-total-by-status
                            class="lg:col-span-4 sm:col-span-6"
                            href="/analytics/contacts"
                        />
                        <analytics-marketing-subscriptions-total-by-status
                            class="lg:col-span-4 sm:col-span-6"
                            status="opt_in"
                            href="/analytics/contacts"
                        />
                        <analytics-outbound-messages-total-contacts-messaged
                            :fetchParams="debouncedFetchParams"
                            useGroupMinDate
                            class="lg:col-span-4 sm:col-span-6"
                            :href="`/analytics/outbound-messages?${stringifyQueryString(debouncedFetchParams)}`"
                        >
                            <template v-slot:title> Total Reached </template>
                            <template v-slot:explainHeader> Total Reached </template>
                            <template v-slot:explainContent>
                                <p>
                                    <strong>Total Reached</strong><br />
                                    Count of unique contacts that were sent a message during the selected period.
                                </p>

                                <p>
                                    <strong>Total Reached %</strong><br />
                                    This shows the change in the number of contacts reached compared to the previous
                                    period. A positive % means more contacts were reached compared to the prior period;
                                    a negative % means fewer were.
                                </p>
                            </template>
                        </analytics-outbound-messages-total-contacts-messaged>
                    </div>

                    <div class="flex flex-col gap-6">
                        <div class="flex xl:flex-row flex-col gap-4 xl:justify-between xl:items-center">
                            <span class="font-bold text-xl">Performance by Category</span>
                            <analytics-performance-legend />
                        </div>

                        <div class="grid gap-6 grid-cols-1 sm:grid-cols-12">
                            <category-metrics-card
                                v-for="category in categories"
                                :key="category.value"
                                :fetchParams="debouncedFetchParams"
                                :category="category"
                                class="col-span-6 2xl:col-span-4"
                            />
                        </div>
                    </div>

                    <category-resources-card
                        v-if="uncategorized"
                        :fetchParams="debouncedFetchParams"
                        :category="uncategorized"
                        :href="`/analytics/franchise-hub/none?${stringifyQueryString(debouncedFetchParams)}`"
                    />
                </div>
            </div>
        </div>
    </page-container>
</template>

<script setup>
import PageContainer from '~/components/general/PageContainer.vue';
import PageHeader from '~/components/general/PageHeader.vue';
import { computed } from 'vue';
import AnalyticsSidebar from './AnalyticsSidebar.vue';
import CategoryMetricsCard from '~/components/analytics/partial/category-metrics/CategoryMetricsCard.vue';
import { useDebouncedFetchParams } from '~/components/analytics/useDebouncedFetchParams';
import { getDateRange, getMinAllowedDate, resourceTypesOptions } from '~/utils/analytics';
import { audienceTypes } from '~/constants/message';
import { SENDER_TYPES } from '~/constants/senderTypes';
import { analyticsCategoriesOptions } from '~/constants/categories';
import AnalyticsMarketingSubscriptionsTotalByStatus from '~/components/analytics/charts/current/AnalyticsMarketingSubscriptionsTotalByStatus.vue';
import AnalyticsOutboundMessagesTotalContactsMessaged from '~/components/analytics/charts/time-series/AnalyticsOutboundMessagesTotalContactsMessaged.vue';
import CategoryResourcesCard from '~/components/analytics/partial/category-metrics/CategoryResourcesCard.vue';
import LoadingBars from '~/components/general/LoadingBars.vue';
import AnalyticsPerformanceLegend from '~/components/analytics/partial/AnalyticsPerformanceLegend.vue';
import { stringifyQueryString } from '~/utils/string';

const categories = computed(() => analyticsCategoriesOptions.filter((c) => c.value !== 'none'));
const uncategorized = computed(() => analyticsCategoriesOptions.find((c) => c.value === 'none'));

const defaultFilters = () => {
    const last30Days = getDateRange('last_30_days');

    return {
        range: [last30Days.start_date, last30Days.end_date],
        filters: {
            sender_types: SENDER_TYPES.map((senderType) => senderType.value),
            audience_types: audienceTypes().map((audienceType) => audienceType.name),
            resource_types: resourceTypesOptions.map((resourceType) => resourceType.value),
        },
    };
};

const { fetchParams, loading, debouncedFetchParams, resetFilters } = useDebouncedFetchParams(defaultFilters());

const minAllowedDate = getMinAllowedDate(true);
</script>
